import { useEffect } from 'react'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { deviceManager } from '../services/device-manager'
import { setSpeakerRoutingFailureHandler } from '../services/audio-output.service'
import { useDevicesStore } from '../store/devices.store'
import { useMeetingSessionStore } from '@/features/meeting/store/meeting-session.store'

/**
 * The app's answer to a device problem: say it out loud, and keep the menus honest.
 *
 * The reason a microphone switch could silently land on the wrong device and
 * stay there was not that nothing noticed — `useLocalMedia` logged it, and the
 * device manager knew. It was that every one of those reports went to a debug
 * channel switched off in production, so the only person who could see the
 * failure was someone already looking for it.
 *
 * Mounted once, high in the tree. Device problems are about the session, not
 * about whichever panel happens to be open.
 */
export function useDeviceIssues(): void {
  const selectMic = useDevicesStore((s) => s.selectMic)
  const selectCam = useDevicesStore((s) => s.selectCam)

  // Output has no `getUserMedia` to fail, so it reports through its own
  // channel — but it is the same class of problem and belongs in the same
  // place: you chose a device and are not getting it.
  useEffect(() => {
    setSpeakerRoutingFailureHandler(() => {
      toast.warning({
        title: "Couldn't use that speaker",
        description: 'Audio is playing through the system default instead.',
      })
    })
    return () => setSpeakerRoutingFailureHandler(null)
  }, [])

  useEffect(
    () =>
      deviceManager.onIssue((issue) => {
        const Device = issue.kind === 'audio' ? 'Microphone' : 'Camera'
        const device = issue.kind === 'audio' ? 'microphone' : 'camera'

        // The browser took the grant away mid-call. Not "pick another device" —
        // the device is there, the permission is gone — so turn it off, record
        // the denial, and raise the recovery dialog, which re-requests and
        // re-opens without a page reload. The Permissions `change` watcher is
        // the primary route; this covers the browsers where that event is
        // unreliable (the capture's own NotAllowedError lands here).
        if (issue.reason === 'permission-revoked') {
          const devices = useDevicesStore.getState()
          const session = useMeetingSessionStore.getState()
          const wasOn = issue.kind === 'audio' ? session.micOn : session.camOn
          devices.setDevicePermission(issue.kind, 'denied')
          if (issue.kind === 'audio') session.setMicOn(false)
          else session.setCamOn(false)
          devices.beginPermissionRecovery(issue.kind, wasOn)
          return
        }

        // A device died and the manager already opened its replacement. Point
        // the stored selection at what is actually running, or the menu goes on
        // showing hardware that has left the building — and, worse, the next
        // deliberate switch back to it compares against an id that is gone.
        //
        // The manager cannot do this itself. It is deliberately outside React
        // and knows nothing about the store; the store imports from `services/`,
        // so a call the other way would close the loop. Same direction the
        // speaker reporter above already travels.
        if (issue.reason === 'disconnected' && issue.recovered?.deviceId) {
          if (issue.kind === 'audio') selectMic(issue.recovered.deviceId)
          else selectCam(issue.recovered.deviceId)
        }

        // Several routes reach a busy camera at once — the meeting's camera-on
        // and the self tile both try to open it — so one id per device keeps it
        // to one notice however many of them failed.
        if (issue.reason === 'in-use') {
          toast.error({
            id: `device-in-use-${issue.kind}`,
            title: `${Device} already in use`,
            description: issue.message,
          })
          return
        }

        // "Pick another one" is the correct thing to say only when there is a
        // decision to make. While the app is switching for you, it describes
        // some other app.
        if (issue.reason === 'disconnected' && issue.recovered) {
          toast.warning({
            title: `${Device} disconnected`,
            description: issue.message,
          })
          return
        }

        // Gone with nothing to move to: the device is off, so say it is off.
        // The flag stayed on, so the button went on showing a live mic or
        // camera that was sending nothing — and since it is also what we
        // report to the room, everyone else's Participants panel did too.
        // Turning it off is a state the user can see and undo; turning it back
        // on asks for a device again.
        if (issue.reason === 'disconnected' && !issue.recovered) {
          const session = useMeetingSessionStore.getState()
          if (issue.kind === 'audio') session.setMicOn(false)
          else session.setCamOn(false)
        }

        const title =
          issue.reason === 'fallback'
            ? `${Device} unavailable`
            : issue.reason === 'disconnected'
              ? `${Device} disconnected`
              : `Couldn't switch ${device}`
        // A dropped stale preference is informational — you still have a working
        // device. A refused switch, or one that vanished with nothing to move
        // to, is an error: you are not sending what you think you are sending.
        const show = issue.reason === 'fallback' ? toast.warning : toast.error
        show({ title, description: issue.message })
      }),
    [selectMic, selectCam],
  )
}
